// MonthlyBarChart.tsx
// Comparativo mensal de vendas (ano atual vs ano anterior)
// Inspirado no gráfico mensal do sistema-producao

import React, { useMemo } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
    ComposedChart, Line, Cell
} from 'recharts';

interface ChartPoint {
    x: string;
    y: number;
}

interface MonthlyBarChartProps {
    currentSeries: ChartPoint[];
    previousSeries: ChartPoint[];
    showComparison?: boolean;
    monthlyGoals?: { mes: number; meta: number; scoreFinal?: number; percVenda?: number; percMargem?: number; percMix?: number }[];
}

const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short'
    }).format(value);
};

const MONTHS = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

const MonthlyBarChart: React.FC<MonthlyBarChartProps> = ({ currentSeries, previousSeries, showComparison = true, monthlyGoals }) => {
    const chartData = useMemo(() => {
        // Se não houver dados, retorna vazio
        if (!currentSeries?.length) return [];

        // Agrupa por mês (extrai YYYY-MM da data)
        const currentByMonth = new Map<string, number>();
        currentSeries.forEach(p => {
            const monthKey = p.x.substring(0, 7); // "2025-01"
            currentByMonth.set(monthKey, (currentByMonth.get(monthKey) || 0) + p.y);
        });

        const previousByMonth = new Map<string, number>();
        if (showComparison) {
            (previousSeries || []).forEach(p => {
                const monthKey = p.x.substring(0, 7);
                previousByMonth.set(monthKey, (previousByMonth.get(monthKey) || 0) + p.y);
            });
        }

        // Pega todos os meses únicos e ordena
        const allMonths = new Set<string>();
        currentByMonth.forEach((_, k) => allMonths.add(k));
        previousByMonth.forEach((_, k) => allMonths.add(k));
        const result = Array.from(allMonths).sort().map(monthKey => {
            const [year, monthNum] = monthKey.split('-');
            const monthIndex = parseInt(monthNum) - 1;
            return {
                month: `${MONTHS[monthIndex]}/${year?.slice(2)}`,
                atual: Math.round(currentByMonth.get(monthKey) || 0),
                ...(showComparison ? { anterior: Math.round(previousByMonth.get(monthKey) || 0) } : {}),
            };
        }).slice(-12); // Últimos 12 meses

        // Adiciona meta mensal e score composto se disponível
        if (monthlyGoals?.length) {
            return result.map(item => {
                const monthNum = MONTHS.indexOf(item.month.split('/')[0]);
                const goalItem = monthlyGoals.find(g => g.mes === monthNum + 1);
                return {
                    ...item,
                    meta: goalItem?.meta ?? null,
                    scoreFinal: goalItem?.scoreFinal,
                    percVenda: goalItem?.percVenda,
                    percMargem: goalItem?.percMargem,
                    percMix: goalItem?.percMix,
                };
            });
        }
        return result;
    }, [currentSeries, previousSeries, monthlyGoals]);

    if (!chartData.length) {
        return (
            <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontWeight: 600, fontSize: 13 }}>
                Sem dados mensais para exibir
            </div>
        );
    }

    const hasGoals = monthlyGoals?.length && monthlyGoals.some(g => g.meta > 0);

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 20, left: 10, bottom: 10 }}
                barCategoryGap="20%"
            >
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fontWeight: 700, fill: 'var(--text-primary, #374151)' }}
                    axisLine={false}
                    tickLine={false}
                />
                <YAxis
                    tickFormatter={formatCurrency}
                    tick={{ fontSize: 10, fontWeight: 700, fill: 'var(--text-secondary, #6B7280)' }}
                    axisLine={false}
                    tickLine={false}
                />
                <Tooltip
                    content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const venda = payload.find((p: any) => p.dataKey === 'atual');
                        const meta = payload.find((p: any) => p.dataKey === 'meta');
                        const d = payload[0]?.payload || {};
                        const scoreFinal = d.scoreFinal;
                        const hasScore = scoreFinal != null;
                        const vendaVal = venda?.value || 0;
                        const metaVal = meta?.value || 0;
                        const passesBySales = metaVal > 0 && vendaVal >= metaVal;
                        const isGoalMet = (scoreFinal != null && scoreFinal >= 98) || passesBySales;
                        return (
                            <div style={{
                                background: 'var(--bg-widget)',
                                borderRadius: 'var(--radius-base, 8px)',
                                padding: '8px 12px',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                                border: '1px solid var(--border-primary)',
                                fontSize: 12,
                                minWidth: 160
                            }}>
                                <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', fontSize: 13, borderBottom: '1px solid var(--border-primary)', paddingBottom: 4 }}>
                                    {label}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
                                    <span style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: 13 }}>Vendas</span>
                                    <span style={{ fontWeight: 700, color: 'var(--text-primary)', fontSize: 14 }}>
                                        {formatCurrency(vendaVal)}
                                    </span>
                                </div>
                                {hasGoals && meta && (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
                                            <span style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: 13 }}>Meta Venda</span>
                                            <span style={{ fontWeight: 700, color: 'var(--theme-chart-start, #dc2626)', fontSize: 14 }}>
                                                {formatCurrency(metaVal)}
                                            </span>
                                        </div>
                                        {hasScore && (
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, paddingTop: 4, borderTop: '1px solid var(--border-primary)', marginTop: 2 }}>
                                                <span style={{ color: 'var(--text-secondary)', fontWeight: 500, fontSize: 13 }}>Resultado</span>
                                                <span style={{ fontWeight: 700, fontSize: 14, color: isGoalMet ? '#16a34a' : '#dc2626' }}>
                                                    {scoreFinal >= 100 ? scoreFinal.toFixed(1) + '%' : scoreFinal.toFixed(2) + '%'}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    }}
                />
                {hasGoals && (
                    <Legend
                        wrapperStyle={{ fontSize: 10, fontWeight: 700, paddingTop: 4 }}
                        iconType="circle"
                    />
                )}
                {hasGoals && (
                    <Line
                        type="monotone"
                        dataKey="meta"
                        name="Meta"
                        stroke="var(--theme-chart-start, #dc2626)"
                        strokeWidth={2.5}
                        strokeDasharray="6 3"
                        dot={{ r: 3, fill: 'var(--theme-chart-start, #dc2626)', strokeWidth: 2, stroke: '#fff' }}
                        connectNulls={false}
                    />
                )}
                <Bar
                    dataKey="atual"
                    name="Vendas"
                    radius={[0, 0, 0, 0]}
                    barSize={32}
                >
                    {chartData.map((entry: Record<string, any>, index) => {
                        const d = entry as { atual: number; meta?: number | null; scoreFinal?: number | null };
                        const percHit = d.scoreFinal ?? (d.meta && d.meta > 0 ? (d.atual / d.meta) * 100 : null);
                        const passesAmount = d.meta != null && d.meta > 0 && d.atual >= d.meta;
                        const atingiuMeta = hasGoals && d.meta != null && (passesAmount || (percHit != null ? percHit >= 98 : d.atual >= d.meta));
                        let fill = 'var(--theme-accent, #1e293b)'; // cor padrão (sem meta ou batido)
                        if (hasGoals && d.meta != null) {
                            fill = atingiuMeta ? 'var(--theme-accent, #1e293b)' : 'rgba(59, 130, 246, 0.4)';
                        }
                        return <Cell key={`cell-${index}`} fill={fill} />;
                    })}
                    <LabelList
                        dataKey="atual"
                        content={(props: any) => {
                            const { value, x, y, width } = props;
                            if (value == null) return null;
                            const text = formatCurrency(value);
                            const textWidth = text.length * 7 + 14;
                            const cx = x + width / 2;
                            return (
                                <g>
                                    <rect
                                        x={cx - textWidth / 2}
                                        y={y - 26}
                                        width={textWidth}
                                        height={20}
                                        rx={6}
                                        fill="rgba(30, 41, 59, 0.7)"
                                        stroke="rgba(71, 85, 105, 0.4)"
                                        strokeWidth={1}
                                    />
                                    <text
                                        x={cx}
                                        y={y - 12}
                                        fill="#ffffff"
                                        fontSize={10}
                                        fontWeight={700}
                                        textAnchor="middle"
                                        fontFamily="Inter, sans-serif"
                                    >
                                        {text}
                                    </text>
                                </g>
                            );
                        }}
                    />
                </Bar>
            </ComposedChart>
        </ResponsiveContainer>
        </div>
    );
};

export default MonthlyBarChart;
